import { useCallback } from "react"

import { useTransportMutation, useTransportQueryUtils } from "@/transport"
import { useQueryClient } from "@tanstack/react-query"

import type { DashboardMetadataResource } from "@/types_api/backend/transport/list_dashboard_metadata_response"

import {
  serializeGetDashboardLayoutRequest,
  serializeUpdateDashboardRequest,
} from "../widgets/utils/transportDashboard"

interface DashboardMetadataListResponse {
  dashboards: DashboardMetadataResource[]
}

const isDashboardResource = (
  dashboard: DashboardMetadataResource,
  dashboardId: string,
) => dashboard.name.split("/").at(-1) === dashboardId

/**
 * Renames a dashboard and writes the name into the cached dashboard list, so the breadcrumb and the sidebar show it before the refetch lands. The list and the dashboard's layout are refetched whether the rename succeeds or fails.
 *
 * @returns A rename that rejects on failure, for `EditableTitle` to keep the field open with the message.
 */
export function useRenameDashboard() {
  const queryClient = useQueryClient()
  const transportQueryUtils = useTransportQueryUtils()
  const updateDashboard = useTransportMutation("updateDashboard")

  return useCallback(
    async (dashboardId: string, displayName: string): Promise<void> => {
      const listQueryKey = transportQueryUtils.getKey("listDashboardMetadata")
      try {
        await updateDashboard.mutateAsync(
          serializeUpdateDashboardRequest({ dashboardId, displayName }),
        )
        queryClient.setQueryData(
          listQueryKey,
          (existingData: DashboardMetadataListResponse | undefined) =>
            existingData && {
              dashboards: existingData.dashboards.map((dashboard) =>
                isDashboardResource(dashboard, dashboardId)
                  ? { ...dashboard, display_name: displayName }
                  : dashboard,
              ),
            },
        )
      } finally {
        void queryClient.invalidateQueries({ queryKey: listQueryKey })
        // The layout response carries the dashboard's metadata too, so its cached name would otherwise stay stale.
        void queryClient.invalidateQueries({
          queryKey: transportQueryUtils.getKey(
            "getDashboardLayout",
            serializeGetDashboardLayoutRequest(dashboardId),
          ),
        })
      }
    },
    [queryClient, transportQueryUtils, updateDashboard],
  )
}
